import {IPolicyApplication, IPremium, IQuotes, ICustomer} from '../models';
import * as moment from 'moment';
import * as document from './documents';
import { getIncludedResource } from '../util';
import { forEach } from 'lodash';
import { fromJsonApi as fromChannelJsonApi } from './channels';

const dateFromApi = (date) => {
  if (! date) {
    return '';
  }

  const parsedDate = moment(date).format('DD/MM/YYYY');

  return parsedDate !== 'Invalid Date' ? parsedDate : '';
};

const dateToApi = (date) => {
  if (! date) {
    return '';
  }

  const formattedDate = moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD');
    // .utcOffset(0, true).format();
  return formattedDate.toLowerCase() !== 'invalid date' ? formattedDate : '';
};

export function fromJsonApi(data, included = []): IPolicyApplication {
  const { id, attributes : { metadata } } = data;

  const premiums: IPremium[] = getIncludedResource(data, included, 'premiums', true).map((premium): IPremium => {
    let scheme = getIncludedResource(premium, included, 'scheme');
    if (scheme === null) {
      scheme = getIncludedResource(data, included, 'schemes');
    }

    const documentsResource = getIncludedResource(data, included, 'channel_scheme_documents', true);
    const documents = {};
    documentsResource.forEach((document) => {
      documents[document.attributes.name] = {
        path: document.attributes.file_path,
        displayName: document.attributes.display_name,
        type: document.attributes.type,
      };
    });

    return {
      id: premium.id,
      schemeType: premium.attributes.metadata.scheme.scheme_type,
      calculatedAt: premium.attributes.metadata.calculated_at,
      validUntil: premium.attributes.metadata.valid_until,
      currency: premium.attributes.currency,
      tax: premium.attributes.tax,
      exTax: premium.attributes.exTax,
      principal: premium.attributes.principal,
      grossExAdditional: premium.attributes.gross_ex_additional,
      stampDuty: premium.attributes.additional_tax_rates,
      gross: premium.attributes.gross,
      benefits: premium.attributes.metadata.benefits.map((benefit) => ({
        name: benefit.name,
        text: benefit.text,
        description: benefit.description,
        inverse: benefit.inverse,
        excess: benefit.excess,
        displayName: benefit.display_name,
        shortName: benefit.short_name,
        subBenefits: benefit.sub_benefits && benefit.sub_benefits.map((subBenefit) => ({
          excess: subBenefit.excess,
          name: subBenefit.name,
          text: subBenefit.text,
        })),
      })),
      information: premium.attributes.metadata.information && {
        fees: premium.attributes.metadata.information.fees,
        standardExcess: premium.attributes.metadata.information.standard_excess,
        boltOns: premium.attributes.metadata.information.bolt_ons,
        upgrade: premium.attributes.metadata.information.upgrade && {
          schemeId: premium.attributes.metadata.information.upgrade.scheme,
          amount: premium.attributes.metadata.information.upgrade.amount,
          coverLevel: premium.attributes.metadata.information.upgrade.coverLevel,
        },
        maxTripDuration: premium.attributes.metadata.information.max_trip_duration,
        startDate: premium.attributes.metadata.information.start_date,
        region: premium.attributes.metadata.information.region,
        tripType: premium.attributes.metadata.information.scheme_type,
      },
      declines: premium.attributes.metadata.declines,
      referrals: premium.attributes.metadata.referrals,
      errors: premium.attributes.metadata.errors && premium.attributes.metadata.errors,
      scheme: {
        id: scheme.id,
        coverLevel: scheme.attributes.cover_level,
        status: scheme.attributes.status,
        logo: scheme.attributes.logo,
        name: scheme.attributes.name,
        insurer: scheme.attributes.insurer,
        documents,
      },
    };
  });

  const customer: ICustomer = getIncludedResource(data, included, 'customer', true).map((customer): ICustomer => {

    return {
      id: customer.id,
      title: customer.attributes.title,
      firstName: customer.attributes.first_name,
      lastName: customer.attributes.last_name,
      customerName: customer.attributes.customer_name,
      dob: dateFromApi(customer.attributes.dob),
      address: customer.attributes.address,
      email: customer.attributes.email,
      phone: customer.attributes.phone,
      status: customer.attributes.status,
      deceased: customer.attributes.deceased,
      metadata: customer.attributes.metadata,
      policies: customer.policies,
      policyApplications: customer.policy_applications,
    };
  });

  const quotes: IQuotes = {};

  premiums.forEach((premium) => {
    if (!quotes[premium.scheme.coverLevel]) {
      quotes[premium.scheme.coverLevel] = {};
    }

    quotes[premium.scheme.coverLevel][premium.schemeType] = premium;
  });

  const quotePremiums = getIncludedResource(data, included, 'policies/premiums', true)
    .map((item) => quotePremiums.fromJsonApi(item, included));

  const documents = getIncludedResource(data, included, 'documents', true)
    .map((item) => document.fromJsonApi(item, included));

  const channel = ((channel) => {
    if (! channel) {
      return;
    }

    return fromChannelJsonApi(channel, included);
  })(getIncludedResource(data, included, 'channel'));

  // noinspection UnnecessaryLocalVariableJS
  const policyApplication: IPolicyApplication = {
    quotationTypes: metadata.quotation_types,
    startDate: dateFromApi(metadata.start_date),
    endDate: dateFromApi(metadata.end_date),
    quotes: metadata.quotes,
    // declarationQuestion: metadata.declaration_question,
    steps: metadata.steps,
    schemeId: metadata.scheme_id,
    quoteReference: data.attributes.quote_reference,
    status: data.attributes.status ?
      data.attributes.status
      : null,
    quotePremiums,
    premiums,
    generateDocuments: false,
    emailSend: false,
    documents,
    channel,
    calculateAllSchemes: false,
    customer,
    renewingPolicyNumber: data.attributes.renewing_policy_number,
    renewal: data.attributes.quote_type === 'renewal',
    quoteType: data.attributes.quote_type,

    backdatedCover: metadata.backdated_cover === 'true' ? true : false,
    insured: {
      id: metadata.insured.id,
      title: metadata.insured.title,
      firstName: metadata.insured.first_name,
      lastName: metadata.insured.last_name,
      email: metadata.insured.email,
      companyName: metadata.insured.company_name,
      country: 'FR',
      metadata: metadata.insured.metadata,
      address: metadata.insured.address,
      phone: metadata.insured.phone,
    },
    revenue: metadata.revenue,
    paymentSchedule: metadata.payment_schedule,
    recurringCard: metadata.recurring_card,
    recurringCardPeriod: metadata.recurring_card_period,
    activities: {
      intermediary: {
        percent: metadata.activities.intermediary.percent,
        amount: metadata.activities.intermediary.amount,
      },
      banking: {
        percent: metadata.activities.banking.percent,
        amount: metadata.activities.banking.amount,
      },
      realEstate: {
        percent: metadata.activities.real_estate.percent,
        amount: metadata.activities.real_estate.amount,
      },
      other: {
        percent: metadata.activities.other.percent,
        amount: metadata.activities.other.amount,
      },
      declaration: metadata.activities.declaration,
    },
    ias: {
      included: metadata.ias.included === 'true' ? true : false,
      primary: metadata.ias.primary === 'true' ? true : false,
      activity: metadata.ias.activity,
      declaration: metadata.ias.declaration,
      payments: metadata.ias.payments,
      paymentsAdjusted: metadata.ias.payments_adjusted,
      insuredPersons: metadata.ias.insured_persons,
    },
    iobsp: {
      included: metadata.iobsp.included === 'true' ? true : false,
      primary: metadata.iobsp.primary === 'true' ? true : false,
      activity: metadata.iobsp.activity,
      declaration: metadata.iobsp.declaration,
      insuredPersons: metadata.iobsp.insured_persons,
    },
    realEstate: {
      included: metadata.real_estate.included === 'true' ? true : false,
      principal: metadata.real_estate.principal,
      representative: {
        title: metadata.real_estate.representative.title,
        firstName: metadata.real_estate.representative.first_name,
        lastName: metadata.real_estate.representative.last_name,
        status: metadata.real_estate.representative.status,
        cardNumber: metadata.real_estate.representative.card_number,
        chamber: metadata.real_estate.representative.chamber,
        issueDate: metadata.real_estate.representative.issue_date,
      },
      comanager: {
        title: metadata.real_estate.comanager.title,
        firstName: metadata.real_estate.comanager.first_name,
        lastName: metadata.real_estate.comanager.last_name,
      },
      comanager1: {
        title: metadata.real_estate.comanager1.title,
        firstName: metadata.real_estate.comanager1.first_name,
        lastName: metadata.real_estate.comanager1.last_name,
      },
    },
    declaration: metadata.declaration,
    issueDeclaration: metadata.issue_declaration,
    currentInsurer: metadata.current_insurer,
    rateOverrides: {
      ias: metadata.rate_overrides.ias,
      iobsp: metadata.rate_overrides.iobsp,
      realEstate: metadata.rate_overrides.real_estate,
    },
    renewalSirenChange: metadata.renewal_siren_change,
    ignoreReferrals: metadata.ignore_referrals,
    renewalRcExploitation: metadata.renewal_rc_exploitation,
  };

  policyApplication.insured.emailConfirmation = metadata.insured.email;

  if (id) {
    policyApplication.id = id;
  }

  if (quotes) {
    policyApplication.quotes = quotes;
  }

  return policyApplication;
}

export function toJsonApi(policyApplication: IPolicyApplication, channelId?: string, brokerId?: string) {
  // Temporary fields
  delete policyApplication.ias.activity._changed;
  delete policyApplication.iobsp.activity._changed;
  delete policyApplication.activities._changed;

  const metadata: any = {
    quotation_types: policyApplication.quotationTypes,
    channel: 'rcp-b2c',
    start_date: dateToApi(policyApplication.startDate),
    end_date: dateToApi(policyApplication.endDate),
    scheme_id: policyApplication.schemeId,
    policy_declaration: policyApplication.policyDeclaration,
    generate_documents: policyApplication.generateDocuments,
    email_send: policyApplication.emailSend,
    steps: policyApplication.steps,

    backdated_cover: policyApplication.backdatedCover,
    insured: {
      title: policyApplication.insured.title,
      first_name: policyApplication.insured.firstName,
      last_name: policyApplication.insured.lastName,
      email: policyApplication.insured.email,
      company_name: policyApplication.insured.companyName,
      country: 'FR',
      metadata: policyApplication.insured.metadata,
      address: policyApplication.insured.address,
      phone: policyApplication.insured.phone,
    },
    revenue: policyApplication.revenue,
    payment_schedule: policyApplication.paymentSchedule,
    recurring_card: policyApplication.recurringCard,
    recurring_card_period: policyApplication.recurringCardPeriod,

    activities: {
      intermediary: {
        percent: policyApplication.activities.intermediary.percent,
        amount: policyApplication.activities.intermediary.amount,
      },
      banking: {
        percent: policyApplication.activities.banking.percent,
        amount: policyApplication.activities.banking.amount,
      },
      real_estate: {
        percent: policyApplication.activities.realEstate.percent,
        amount: policyApplication.activities.realEstate.amount,
      },
      other: {
        percent: policyApplication.activities.other.percent,
        amount: policyApplication.activities.other.amount,
      },
      declaration: policyApplication.activities.declaration,
    },
    ias: {
      included: policyApplication.ias.included.toString(),
      primary: policyApplication.ias.primary.toString(),
      activity: policyApplication.ias.activity,
      declaration: policyApplication.ias.declaration,
      payments: policyApplication.ias.payments,
      payments_adjusted: policyApplication.ias.paymentsAdjusted,
      insured_persons: policyApplication.ias.insuredPersons,
    },
    iobsp: {
      included: policyApplication.iobsp.included.toString(),
      primary: policyApplication.iobsp.primary.toString(),
      activity: policyApplication.iobsp.activity,
      declaration: policyApplication.iobsp.declaration,
      insured_persons: policyApplication.iobsp.insuredPersons,
    },
    real_estate: {
      included: policyApplication.realEstate.included.toString(),
      principal: policyApplication.realEstate.principal,
      representative: {
        title: policyApplication.realEstate.representative.title,
        first_name: policyApplication.realEstate.representative.firstName,
        last_name: policyApplication.realEstate.representative.lastName,
        status: policyApplication.realEstate.representative.status,
        card_number: policyApplication.realEstate.representative.cardNumber,
        chamber: policyApplication.realEstate.representative.chamber,
        issue_date: policyApplication.realEstate.representative.issueDate,
      },
      comanager: {
        title: policyApplication.realEstate.comanager.title,
        first_name: policyApplication.realEstate.comanager.firstName,
        last_name: policyApplication.realEstate.comanager.lastName,
      },
      comanager1: {
        title: policyApplication.realEstate.comanager1.title,
        first_name: policyApplication.realEstate.comanager1.firstName,
        last_name: policyApplication.realEstate.comanager1.lastName,
      },
    },
    declaration: policyApplication.declaration,
    issue_declaration: policyApplication.issueDeclaration,
    current_insurer: policyApplication.currentInsurer,
    rate_overrides: {
      ias: policyApplication.rateOverrides.ias,
      iobsp: policyApplication.rateOverrides.iobsp,
      real_estate: policyApplication.rateOverrides.realEstate,
    },
    renewal_siren_change: policyApplication.renewalSirenChange,
    ignore_referrals: policyApplication.ignoreReferrals,
    renewal_rc_exploitation: policyApplication.renewalRcExploitation,
  };

  if (!metadata.insured.first_name.length) {
    metadata.insured.first_name = 'Test';
  }
  if (!metadata.insured.last_name.length) {
    metadata.insured.last_name = 'Test';
  }

  const meta = {
    customer: {
      create_account: policyApplication.createAccount ? policyApplication.createAccount : false,
      password: policyApplication.password,
      password_confirmation: policyApplication.passwordConfirmation,
    },
    payment_type: policyApplication.paymentType,
  };

  forEach(policyApplication.options, (value, key) => {
    if (value === 'true') {
      value = true;
    }

    if (value === 'false') {
      value = false;
    }

    metadata.options[key] = value;
  });

  // noinspection UnnecessaryLocalVariableJS
  const data: any = {
    type: 'application',
    attributes: {
      metadata,
      quote_reference: policyApplication.quoteReference,
      quote_type: policyApplication.quoteType,
      policy_number: policyApplication.renewingPolicyNumber,
    },
    meta,
    relationships: {},
  };

  if (policyApplication.schemeId && ! policyApplication.calculateAllSchemes) {
    data.relationships.schemes = {
      data: [
        { id: policyApplication.schemeId },
      ],
    };
  }

  if (policyApplication.id) {
    data.id = policyApplication.id;
  }

  if (channelId) {
    data.relationships.channel = {
      data: {
        id: channelId,
      },
    };
  }

  if (brokerId) {
    data.relationships.broker = {
      data: {
        id: brokerId,
      },
    };
  }

  return data;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/data/api/transformers/application.ts